import {
  Button,
  Collapse,
  DatePicker,
  DatePickerProps,
  Form,
  Input,
  message,
  TimePicker,
  TimePickerProps,
  Tooltip,
} from "antd";
import { useFetcher, useLoaderData } from "react-router-dom";
import {
  INTENTS,
  parseDuration,
  durationToDayjs,
  mergeDateAndTime,
  dateFormat,
  addDurationTo,
} from "~/utils";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { AddReservationPageLoaderType } from "./loaders/AddReservationPageLoader";
import { ClockCircleOutlined } from "@ant-design/icons";
import { useMediaQuery } from "@mui/material";
import { useState } from "react";
import Title from "antd/es/typography/Title";
import { CustomInputNumber } from "~/components";
import { DurationPicker } from "./components";
import { useForm } from "antd/es/form/Form";

import * as APITypes from "~/API";
import { ReservationRules } from "../components";
import { Duration, TimeFrame } from "~/types";
import { useAppTranslation } from "~/hooks/useAppTranslation";

var weekday = require("dayjs/plugin/weekday");
dayjs.extend(weekday);

export const AddReservationPage = () => {
  const [formInstance] = useForm();
  const loaderData = useLoaderData() as AddReservationPageLoaderType;
  const { t } = useAppTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | undefined>(
    undefined
  );
  const [selectedTime, setSelectedTime] = useState<dayjs.Dayjs | undefined>(
    undefined
  );
  const fetcher = useFetcher();
  dayjs.extend(customParseFormat);

  const formattedAmenity = loaderData.amenity.formattedAmenityDetails;
  const amenityDetails = formattedAmenity.amenityDetails;
  const reservations = formattedAmenity.reservations;
  const reservationRule = amenityDetails.reservationRule;

  const atMaxRequests = reservationRule.maxRequests
    ? reservations.filter(
        (reservation) => reservation.data.tenantID === loaderData.userID
      ).length >= reservationRule.maxRequests
    : false;

  const smallScreen = useMediaQuery("(max-width: 340px)");

  const handleSubmit = async (formValues: any) => {
    try {
      const intent = INTENTS._amenity_create_reservation;
      const startDate = mergeDateAndTime(
        formValues.startDate,
        formValues.startTime
      );
      const duration = parseDuration(formValues.duration);
      if (!duration)
        throw new Error(t("An_error_occured_duration_not_defined"));
      const endDate = addDurationTo({ date: startDate, duration });
      const values = {
        details: formValues.details,
        status: reservationRule.requiresApproval
          ? APITypes.AmenityReservationStatus.Pending
          : APITypes.AmenityReservationStatus.Approved,
        startDate: dateFormat(startDate, "DD-MM-YYYY HH:mm"),
        endDate: dateFormat(endDate, "DD-MM-YYYY HH:mm"),
        partySize: formValues.partySize,
      };
      fetcher.submit(
        { body: JSON.stringify(values), intent: intent },
        { method: "post" }
      );
      setSubmitted(true);
      message.success(t("Form_submitted_successfully"));
    } catch (error) {
      console.error("Error submitting form:", error);
      message.error(t("Error_submitting_form"));
    }
  };

  const closestDur = parseDuration(reservationRule.closestTime ?? "");
  const limitClose = closestDur && closestDur.num !== 0;
  const furthestDur = parseDuration(reservationRule.furthestTime ?? "");
  const limitFar = furthestDur && furthestDur.num !== 0;

  const maxDur = parseDuration(reservationRule.maxDuration ?? "");
  const limitMax = maxDur && maxDur.num !== 0;

  const minDur = parseDuration(reservationRule.minDuration ?? "");
  const limitMin = minDur && minDur.num !== 0;
  const minRange = limitMin ? durationToDayjs(minDur.duration) : undefined;

  const getClosest = () => {
    const now = dayjs();
    return limitClose
      ? addDurationTo({ date: now, duration: closestDur })
      : now;
  };

  const getFurthest = () => {
    const now = dayjs();
    return limitFar
      ? addDurationTo({ date: now, duration: furthestDur })
      : undefined;
  };

  const createRange = (start: number, end: number) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };

  const exceptions = loaderData.amenity.availability.unavailable;
  const availability = loaderData.amenity.availability.available;

  const addRange = (arr: number[], rangeStart: number, rangeEnd: number) => {
    for (let i = rangeStart; i < rangeEnd; i++) {
      arr[i] = i;
    }
  };

  const addRangeTo = (
    minutes: number[][],
    rangeStart: number,
    rangeEnd: number,
    hour: number
  ) => {
    if (!minutes[hour]) {
      minutes[hour] = [];
    }
    addRange(minutes[hour], rangeStart, rangeEnd);
  };

  const parseException = (
    day: dayjs.Dayjs,
    timeFrame: TimeFrame,
    hours: number[],
    minutes: number[][]
  ) => {
    const startTime = timeFrame.start;
    const start = limitMin
      ? startTime.subtract(minDur.num, minRange)
      : startTime;
    const end = timeFrame.end;
    const hourStart = start.hour();
    const hourEnd = end.hour();
    const startOnSelected = day.isSame(start, "day");
    const endOnSelected = day.isSame(end, "day");
    if (startOnSelected && endOnSelected) {
      if (hourStart !== hourEnd) {
        addRange(hours, hourStart + 1, hourEnd);
        addRangeTo(minutes, start.minute(), endOfHour, hourStart);
        addRangeTo(minutes, 0, end.minute(), hourEnd);
      } else {
        addRangeTo(minutes, start.minute(), end.minute(), hourStart);
      }
    } else if (startOnSelected) {
      addRange(hours, hourStart + 1, endOfDay);
      addRangeTo(minutes, start.minute(), endOfHour, hourStart);
    } else if (endOnSelected) {
      addRange(hours, 0, hourEnd);
      addRangeTo(minutes, 0, end.minute(), hourEnd);
    } else if (day.isAfter(start, "day") && day.isBefore(end, "day")) {
      addRange(hours, 0, endOfDay);
    }
  };

  const endOfHour = 60;
  const endOfDay = 24;

  const computeDisabledTimes = (day?: dayjs.Dayjs) => {
    if (!day) {
      return {
        disabledHours: () => createRange(0, endOfDay),
        disabledMinutes: () => createRange(0, endOfHour),
      };
    }

    let hours: number[] = [];
    let minutes: number[][] = [];

    const closest = getClosest();
    const furthest = getFurthest();

    // Prevent selection of time that is too close to now
    if (day.isSame(closest, "day")) {
      const hour = closest.hour();
      addRange(hours, 0, hour);
      addRangeTo(minutes, 0, closest.minute(), hour);
    }
    // Prevent selection of time that is too far from now
    if (furthest && day.isSame(furthest, "day")) {
      const hour = furthest.hour();
      addRange(hours, hour + 1, endOfDay);
      addRangeTo(minutes, closest.minute(), endOfHour, hour);
    }

    //@ts-ignore
    const weekday = day.weekday();
    const dayAvail = availability[weekday];
    // Prevent selection of time outside of normal availability
    for (let i = 0; i < dayAvail.length; i++) {
      const hourStart = dayAvail[i].start.hour();
      addRange(hours, 0, hourStart);
      addRangeTo(minutes, 0, dayAvail[i].start.minute(), hourStart);

      const endTime = dayAvail[i].end;
      const end = limitMin ? endTime.subtract(minDur.num, minRange) : endTime;
      const hourEnd = end.hour();
      addRange(hours, hourEnd + 1, endOfDay);
      addRangeTo(minutes, end.minute(), endOfHour, hourEnd);
    }

    // Prevent selection of time within an exception time frame
    for (let i = 0; i < exceptions.length; i++) {
      parseException(day, exceptions[i].timeFrame, hours, minutes);
    }

    // Prevent selection at the same time as a pending request (of this tenant)
    for (let i = 0; i < reservations.length; i++) {
      if (
        reservations[i].data.status !==
          APITypes.AmenityReservationStatus.Rejected &&
        reservations[i].data.status !==
          APITypes.AmenityReservationStatus.Canceled
      ) {
        parseException(day, reservations[i].timeFrame, hours, minutes);
      }
    }

    for (let i = 0; i < endOfDay; i++) {
      // remove empty items
      minutes[i] = minutes[i]?.filter((item) => item !== undefined);
      // if the hour is already disabled, don't worry about it
      if (hours[i] !== undefined) continue;
      // otherwise, check if all minutes in hour are disabled,
      // and disable the hour if so
      if (minutes[i]?.length === endOfHour) hours[i] = i;
    }

    // remove empty items
    hours = hours.filter((item) => item !== undefined);

    return {
      disabledHours: () => hours,
      disabledMinutes: (hour: number) => minutes[hour] ?? [],
    };
  };

  const computedDisabledTimes = computeDisabledTimes(selectedDate);

  const disabledTimes: TimePickerProps["disabledTime"] = (_) => {
    return computedDisabledTimes;
  };

  const disabledDates: DatePickerProps["disabledDate"] = (query) => {
    // return true to disable date
    if (
      (limitMax && maxDur.num <= 0) ||
      loaderData.amenity.availability.fullyUnavailable
    )
      return true;

    for (let i = 0; i < exceptions.length; i++) {
      if (
        query.isAfter(exceptions[i].timeFrame.start, "day") &&
        query.isBefore(exceptions[i].timeFrame.end, "day")
      ) {
        return true;
      }
    }

    //@ts-ignore
    const weekday = query.weekday();
    if (availability[weekday].length === 0) return true;

    if (computeDisabledTimes(query).disabledHours().length === endOfDay)
      return true;

    return false;
  };

  return (
    <>
      <Title level={2}>
        {amenityDetails.name}:{" "}
        {reservationRule.requiresApproval
          ? t("Request_Reservation")
          : t("Make_Reservation")}
      </Title>
      <Collapse
        size="small"
        items={[
          {
            key: 1,
            label: t("Rules"),
            children: <ReservationRules reservationRule={reservationRule} />,
          },
        ]}
      />
      <br />
      <Form
        form={formInstance}
        onFinish={handleSubmit}
        className="form-grid"
        scrollToFirstError
        initialValues={{
          duration: limitMin ? `${minDur.num} ${minDur.duration}` : "0 m",
          partySize: 1,
        }}
      >
        <Form.Item
          label={t("Start_Date")}
          name="startDate"
          rules={[
            {
              required: true,
              message: t("Must_specify_date_date"),
            },
          ]}
        >
          <DatePicker
            format="DD MMM YYYY"
            suffixIcon={!smallScreen && <ClockCircleOutlined />}
            allowClear={false}
            minDate={getClosest()}
            maxDate={getFurthest()}
            defaultOpenValue={getClosest().add(1, "day")}
            onChange={setSelectedDate}
            disabledDate={disabledDates}
          />
        </Form.Item>
        <Form.Item
          label={t("Start_Time")}
          name="startTime"
          rules={[
            {
              validator: (_, value: dayjs.Dayjs) => {
                if (!value)
                  return Promise.reject(
                    new Error(t("Must_specify_start_time"))
                  );
                const times = disabledTimes(dayjs());
                const hour = value.hour();
                if (
                  times.disabledHours?.().includes(hour) ||
                  times.disabledMinutes?.(hour).includes(value.minute())
                ) {
                  return Promise.reject(new Error(t("Select_valid_time")));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <TimePicker
            format="hh:mm A"
            minuteStep={5}
            suffixIcon={!smallScreen && <ClockCircleOutlined />}
            allowClear={false}
            disabledTime={disabledTimes}
            disabled={!selectedDate}
            onChange={setSelectedTime}
          />
        </Form.Item>
        <Form.Item
          label={t("Duration")}
          name="duration"
          rules={[
            {
              validator: (_, value: string) => {
                const duration = parseDuration(value);
                if (!duration)
                  return Promise.reject(new Error(t("Select_duration")));
                const times = disabledTimes(dayjs());

                if (!selectedTime) return Promise.resolve();
                const endExclusive = addDurationTo({
                  date: selectedTime,
                  duration,
                });
                const end = endExclusive.subtract(minDur?.num ?? 0, minRange);

                const hour = end.hour();

                if (
                  times.disabledHours?.().includes(hour) ||
                  times.disabledMinutes?.(hour).includes(end.minute())
                ) {
                  return Promise.reject(
                    new Error(t("Reservation_cannot_extend"))
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DurationPicker
            choicesOf={[Duration.Minute, Duration.Hour]}
            min={minDur}
            max={maxDur}
          />
        </Form.Item>

        <Form.Item
          label={t("Party_size")}
          name={"partySize"}
          rules={
            reservationRule.maxPartySize
              ? [
                  {
                    validator: (_, value) => {
                      if (value > (reservationRule.maxPartySize ?? 0)) {
                        return Promise.reject(
                          new Error(t("Party_size_is_too_large"))
                        );
                      }
                      return Promise.resolve();
                    },
                    warningOnly: reservationRule.requiresApproval ?? false,
                  },
                ]
              : []
          }
        >
          <CustomInputNumber whole min={1} />
        </Form.Item>

        <Form.Item label={t("Details")} name="details">
          <Input.TextArea placeholder={t("Enter_a_description")} />
        </Form.Item>

        <Form.Item>
          <Tooltip
            title={atMaxRequests ? t("Max_Allowed_Request_Reached") : ""}
          >
            <Button
              type="primary"
              htmlType="submit"
              loading={submitted}
              disabled={atMaxRequests}
            >
              {t("Submit")}
            </Button>
          </Tooltip>
        </Form.Item>
      </Form>
    </>
  );
};
